import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  MAIL_SEND_STATUS_LABELS,
  can,
  type DocumentRef,
  type MailSendStatus,
} from "@devengo/shared";

import { api } from "../lib/api";
import { useCurrentUser } from "../lib/session";
import { Badge, Button, ErrorBox, Field, Input, Notice } from "./ui";

export interface MailSend {
  id: string;
  companyId: string;
  documentLabel: string;
  attachmentName: string | null;
  recipients: string[];
  subject: string;
  bodyText: string;
  status: MailSendStatus;
  failureReason: string | null;
  sentByName: string | null;
  createdAt: string;
  queuedAt: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
}

interface Contact {
  id: string;
  name: string;
  emails: string[];
}

export function statusTone(status: MailSendStatus): "success" | "warning" | "danger" | "neutral" {
  if (status === "delivered" || status === "sent") return "success";
  if (status === "bounced" || status === "failed" || status === "complained") return "danger";
  if (status === "unknown" || status === "queued") return "warning";
  return "neutral";
}

/**
 * "Enviar por mail" for a generated document (spec 015 Story 2). Creating the draft stores the exact
 * file; the Dueño reviews recipients, subject, text and the attachment itself, then confirms.
 */
export function SendDocumentButton({
  companyId,
  document,
}: {
  companyId: string;
  document: DocumentRef;
}) {
  const user = useCurrentUser();
  const [key, setKey] = useState(() => crypto.randomUUID());
  // Another period or book is another operation: it must not replay the previous draft's key.
  const documentKey = JSON.stringify(document);
  useEffect(() => setKey(crypto.randomUUID()), [documentKey]);
  const [draft, setDraft] = useState<MailSend | null>(null);
  const status = useQuery({
    queryKey: ["mail", "status"],
    queryFn: () => api.get<{ configured: boolean }>("/api/mail/status"),
    staleTime: 5 * 60_000,
    enabled: can(user.role, "sendDocuments"),
  });
  const create = useMutation({
    mutationFn: () =>
      api.postOnce<MailSend>(`/api/companies/${companyId}/mail-sends`, { document }, key),
    onSuccess: (created) => {
      setDraft(created);
      setKey(crypto.randomUUID());
    },
  });
  if (!can(user.role, "sendDocuments") || !status.data?.configured) return null;
  return (
    <>
      <Button variant="secondary" onClick={() => create.mutate()} disabled={create.isPending}>
        Enviar por mail
      </Button>
      {create.error ? (
        <div className="basis-full">
          <ErrorBox error={create.error} />
        </div>
      ) : null}
      {draft ? <SendDialog draft={draft} onClose={() => setDraft(null)} /> : null}
    </>
  );
}

function SendDialog({ draft, onClose }: { draft: MailSend; onClose: () => void }) {
  const client = useQueryClient();
  const contacts = useQuery({
    queryKey: ["contacts", draft.companyId],
    queryFn: () => api.get<Contact[]>(`/api/companies/${draft.companyId}/contacts`),
  });
  const [recipients, setRecipients] = useState<string[]>(draft.recipients);
  const [subject, setSubject] = useState(draft.subject);
  const [bodyText, setBodyText] = useState(draft.bodyText);
  const [sentId, setSentId] = useState<string | null>(null);
  const confirm = useMutation({
    mutationFn: () =>
      api.post<MailSend>(`/api/mail-sends/${draft.id}/send`, { recipients, subject, bodyText }),
    onSuccess: async (sent) => {
      setSentId(sent.id);
      await client.invalidateQueries({ queryKey: ["mail-sends", draft.companyId] });
    },
  });
  const discard = useMutation({
    mutationFn: () => api.post(`/api/mail-sends/${draft.id}/discard`),
    onSuccess: onClose,
  });
  const tracking = useQuery({
    queryKey: ["mail-send", sentId],
    queryFn: () => api.get<MailSend>(`/api/mail-sends/${sentId}`),
    enabled: sentId !== null,
    refetchInterval: (query) => (query.state.data?.status === "queued" ? 3000 : false),
  });
  const addresses = [...new Set(contacts.data?.flatMap((c) => c.emails) ?? [])];
  const toggle = (address: string) =>
    setRecipients((list) =>
      list.includes(address) ? list.filter((a) => a !== address) : [...list, address],
    );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Enviar por mail"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-xl rounded-lg bg-white p-5 shadow-lg">
        <h2 className="mb-1 text-lg font-semibold">Enviar por mail</h2>
        <p className="mb-4 text-sm text-brand-muted">
          {draft.documentLabel} ·{" "}
          <a
            className="text-brand-primary underline"
            href={`/api/mail-sends/${draft.id}/attachment`}
            target="_blank"
            rel="noreferrer"
          >
            ver el adjunto exacto{draft.attachmentName ? ` (${draft.attachmentName})` : ""}
          </a>
        </p>
        {sentId ? (
          <div className="space-y-3">
            <Notice tone={tracking.data?.status === "queued" ? "info" : "success"}>
              Estado:{" "}
              <Badge tone={statusTone(tracking.data?.status ?? "queued")}>
                {MAIL_SEND_STATUS_LABELS[tracking.data?.status ?? "queued"]}
              </Badge>{" "}
              {tracking.data?.failureReason ?? ""}
            </Notice>
            <p className="text-xs text-brand-muted">
              Queda registrado en Contacto, con quién lo mandó, a quién y qué archivo.
            </p>
            <div className="flex justify-end">
              <Button onClick={onClose}>Cerrar</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <fieldset>
              <legend className="mb-1 text-sm font-medium">Para</legend>
              <div className="flex flex-col gap-1 text-sm">
                {addresses.map((address) => (
                  <label key={address} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={recipients.includes(address)}
                      onChange={() => toggle(address)}
                    />
                    {address}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-brand-muted">
                Solo los mails cargados en los contactos de la empresa.
              </p>
            </fieldset>
            <Field label="Asunto">
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </Field>
            <Field label="Mensaje">
              <textarea
                className="min-h-40 w-full rounded-md border border-brand-line px-2.5 py-1.5 text-sm outline-none focus:border-brand-primary"
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
              />
            </Field>
            <ErrorBox error={confirm.error ?? discard.error} />
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => discard.mutate()}
                disabled={discard.isPending}
              >
                Descartar
              </Button>
              <Button
                onClick={() => confirm.mutate()}
                disabled={confirm.isPending || recipients.length === 0}
              >
                Enviar a {recipients.length}{" "}
                {recipients.length === 1 ? "destinatario" : "destinatarios"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
